/**
 * FieldGuide Integration Utilities
 *
 * This file contains utility functions for detecting and handling FieldGuide integration
 * in client requests. FieldGuide is an external request management system that can be
 * synchronized with Aiwyn's client request system.
 */

/**
 * Extended interface for client requests that may have FieldGuide integration
 */
export interface ClientRequestWithFieldGuide {
  sysRefs?: Record<string, any>;
  [key: string]: any;
}

/**
 * Determines if a client request has FieldGuide integration by checking for
 * field_guide references in the sysRefs data structure.
 *
 * FieldGuide requests are external requests that have been synchronized via webhook
 * and stored as client_request records in Aiwyn. When a client request contains
 * field_guide references in sysRefs, it indicates this request is managed by
 * FieldGuide and should display FieldGuide-specific UI elements.
 *
 * @param clientRequest - The client request entity to check
 * @returns true if the request has field_guide references, false otherwise
 *
 * @example
 * // Example data structure with field_guide reference
 * const fieldGuideRequest = {
 *   id: 1,
 *   name: "SOC 2 Audit Request List",
 *   sysRefs: {
 *     field_guide: {
 *       ref: "7c19dbf6-86dc-41a2-ad7d-28d807135e2f"
 *     },
 *     field_guide_deep_link: {
 *       href: "https://app.fieldguide.io/engagements/27064/requests",
 *       type: "text/html",
 *       title: "View this Request in Fieldguide"
 *     }
 *   }
 * };
 *
 * isFieldGuideRequest(fieldGuideRequest); // returns true
 *
 * // Example data structure without field_guide reference
 * const aiwynRequest = {
 *   id: 2,
 *   name: "Tax Compliance Review",
 *   sysRefs: {}
 * };
 *
 * isFieldGuideRequest(aiwynRequest); // returns false
 */
export function isFieldGuideRequest(clientRequest: ClientRequestWithFieldGuide): boolean {
  if (!clientRequest?.sysRefs) {
    return false;
  }

  return Object.keys(clientRequest.sysRefs).some((key) => clientRequest.sysRefs[key]?.ref && key.includes('field_guide'));
}

/**
 * Gets the FieldGuide deep link URL for a client request.
 *
 * @param clientRequest - The client request entity
 * @returns The FieldGuide deep link URL or null if not available
 */
export function getFieldGuideDeepLink(clientRequest: ClientRequestWithFieldGuide): string | null {
  if (clientRequest?.sysRefs?.field_guide_deep_link?.href) {
    return clientRequest.sysRefs.field_guide_deep_link.href;
  } else if (clientRequest?.sysRefs?.field_guide_deep_link?.ref) {
    return clientRequest.sysRefs.field_guide_deep_link.ref;
  }
  return null;
}

/**
 * Opens a FieldGuide request in a new tab.
 *
 * @param clientRequest - The client request entity
 */
export function openFieldGuideRequest(clientRequest: ClientRequestWithFieldGuide): void {
  const deepLink = getFieldGuideDeepLink(clientRequest);

  if (deepLink) {
    window.open(deepLink, '_blank');
  }
}
